import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  ClinicForSharingDto,
  ReportVisibilityService,
} from './report-visibility.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { translate } from '@jsverse/transloco';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { ConfirmationDialogComponent } from '../../../../../shared/component/confirmation-dialog/confirmation-dialog.component';

export interface ReportVisibilityDialogData {
  healthRecordUid: string;
  clinicName?: string;
}

@Component({
  selector: 'anihis-report-visibility-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatDialogActions,
    MatButtonModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslocoModule,
    InfiniteScrollDirective,
  ],
  templateUrl: './report-visibility-dialog.component.html',
  styleUrl: './report-visibility-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportVisibilityDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<ReportVisibilityDialogComponent>);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly data = inject<ReportVisibilityDialogData>(MAT_DIALOG_DATA);
  private readonly visibilityService = inject(ReportVisibilityService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  private readonly pageSize = 20;
  private pageNumber = 1;

  loading = signal(true);
  saving = signal(false);
  loadingClinics = signal(false);
  loadingMore = signal(false);
  hasNextPage = signal(false);
  visibleToAllClinics = signal(true);
  clinics = signal<ClinicForSharingDto[]>([]);
  searchControl = new FormControl<string>('');
  visibilityControl = new FormControl(true, { nonNullable: true });

  constructor() {
    this.visibilityService
      .getVisibility(this.data.healthRecordUid)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (result) => {
          this.visibleToAllClinics.set(result.visibleToAllClinics);
          this.visibilityControl.setValue(result.visibleToAllClinics, {
            emitEvent: false,
          });
          if (!result.visibleToAllClinics) {
            this.loadClinics();
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.snackBar.open(translate('An error occurred'), '✕', {
            panelClass: ['snackbar-error'],
          });
          this.cdr.markForCheck();
        },
      });

    this.visibilityControl.valueChanges.subscribe((checked) => {
      if (this.loading() || this.saving()) {
        return;
      }
      this.onVisibilityToggle(checked);
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        if (!this.visibleToAllClinics()) {
          this.loadClinics();
        }
      });
  }

  loadClinics(reset = true): void {
    if (this.visibleToAllClinics()) {
      return;
    }

    if (reset) {
      this.pageNumber = 1;
      this.hasNextPage.set(false);
      this.loadingClinics.set(true);
    } else if (
      !this.hasNextPage() ||
      this.loadingMore() ||
      this.loadingClinics()
    ) {
      return;
    } else {
      this.loadingMore.set(true);
    }

    this.visibilityService
      .getClinicsForSharing(
        this.data.healthRecordUid,
        this.searchControl.value ?? undefined,
        this.pageNumber,
        this.pageSize,
      )
      .pipe(
        finalize(() => {
          this.loadingClinics.set(false);
          this.loadingMore.set(false);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (result) => {
          const items = result.clinics ?? [];
          if (reset) {
            this.clinics.set(items);
          } else {
            this.clinics.update((current) => [...current, ...items]);
          }
          this.hasNextPage.set(!!result.hasNextPage);
          this.pageNumber++;
        },
        error: () => {
          this.snackBar.open(translate('An error occurred'), '✕', {
            panelClass: ['snackbar-error'],
          });
        },
      });
  }

  loadMoreClinics(): void {
    this.loadClinics(false);
  }

  onVisibilityToggle(checked: boolean): void {
    const previous = this.visibleToAllClinics();
    if (checked === previous) {
      return;
    }

    this.visibleToAllClinics.set(checked);
    if (checked) {
      this.clinics.set([]);
      this.searchControl.setValue('', { emitEvent: false });
    }

    this.saving.set(true);
    this.visibilityService
      .updateVisibility(this.data.healthRecordUid, {
        visibleToAllClinics: checked,
      })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          if (!checked) {
            this.loadClinics();
          }
        },
        error: () => {
          this.visibleToAllClinics.set(previous);
          this.visibilityControl.setValue(previous, { emitEvent: false });
          this.snackBar.open(translate('An error occurred'), '✕', {
            panelClass: ['snackbar-error'],
          });
          this.cdr.markForCheck();
        },
      });
  }

  toggleClinicShare(clinic: ClinicForSharingDto): void {
    if (this.saving()) return;

    if (clinic.isShared) {
      const ref = this.dialog.open(ConfirmationDialogComponent, {
        width: '400px',
        disableClose: true,
        data: {
          title: translate('revoke_report_share_title'),
          text: translate('revoke_report_share_text', {
            clinicName: clinic.name,
          }),
        },
      });

      ref
        .afterClosed()
        .pipe(take(1))
        .subscribe((res) => {
          if (res?.confirmed) {
            this.executeClinicShareToggle(clinic);
          }
        });
      return;
    }

    this.executeClinicShareToggle(clinic);
  }

  private executeClinicShareToggle(clinic: ClinicForSharingDto): void {
    if (this.saving()) return;

    this.saving.set(true);
    const action$ = clinic.isShared
      ? this.visibilityService.revokeShare(this.data.healthRecordUid, clinic.uid)
      : this.visibilityService.shareHealthRecord(this.data.healthRecordUid, {
          clinicUid: clinic.uid,
        });

    action$
      .pipe(
        switchMap(() => {
          if (!clinic.isShared) {
            this.visibleToAllClinics.set(false);
            this.visibilityControl.setValue(false, { emitEvent: false });
          }
          return of(null);
        }),
        finalize(() => this.saving.set(false)),
      )
      .subscribe({
        next: () => this.loadClinics(),
        error: () => {
          this.snackBar.open(translate('An error occurred'), '✕', {
            panelClass: ['snackbar-error'],
          });
        },
      });
  }

  close(): void {
    this.dialogRef.close({ updated: true });
  }
}
